import locales from '~/config/locales'
import {
  hasSuppressingQuery,
  pickXDefaultHref,
  stripHreflangTrailingSlash,
} from '../utils/hreflang'

export interface UseStaticMetaLinksOptions {
  /** Locale codes to exclude from alternates (e.g. shops/countries where this path 404s). */
  excludedLocales?: string[]
}

export default (path: string, options: UseStaticMetaLinksOptions = {}) => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const availableShops = useAvailableShops()
  const currentShop = useCurrentShop()
  const excludedLocales = options.excludedLocales ?? []

  return computed(() => {
    // Suppress all hreflang when the URL carries any query key other than `page`
    // (e.g. search's ?term=, filters/sort): such a view is not a canonical
    // hreflang group member (ECOM-2433). This builder emits hreflang only —
    // canonical is generated separately and continues to strip params.
    if (hasSuppressingQuery(route.query)) {
      return []
    }

    const baseUrl = (config.public.baseUrl as string)?.replace(/\/$/, '') || ''
    const pathSuffix = path !== '' ? `/${path}` : ''
    const currentLocale = currentShop.value.locale
    const currentIso =
      locales.find((l) => l.code === currentLocale)?.iso ?? currentLocale
    const selfHref = stripHreflangTrailingSlash(
      `${baseUrl}/${currentLocale}${pathSuffix}`,
    )

    // Exclude locales where this path is unavailable (avoids hreflang links to 404s)
    const otherShops = availableShops.value.filter(
      (item) =>
        item.shopId !== currentShop.value.shopId &&
        !excludedLocales.includes(item.locale),
    )
    const alternateLinks = otherShops.map((shop) => ({
      rel: 'alternate' as const,
      hreflang:
        locales.find((l) => l.code === shop.locale)?.iso ??
        shop.isoLanguageCode,
      href: stripHreflangTrailingSlash(`${baseUrl}/${shop.locale}${pathSuffix}`),
    }))

    // Self-reference is mandatory for hreflang (Google SEO)
    const selfReference = {
      rel: 'alternate' as const,
      hreflang: currentIso,
      href: selfHref,
    }

    // x-default = the de-DE member verbatim (the self-reference carries it on
    // the German shop); omit when de is excluded rather than linking to a 404.
    const members = [selfReference, ...alternateLinks]
    const xDefaultHref = pickXDefaultHref(members)
    const xDefault = xDefaultHref
      ? {
          rel: 'alternate' as const,
          hreflang: 'x-default',
          href: xDefaultHref,
        }
      : undefined

    return [...members, ...(xDefault ? [xDefault] : [])]
  })
}
